import React from "react";
import classNames from "classnames/bind";
import { ChevronDown } from "lucide-react";
import styles from "../ProfilePage.module.scss";
import { useLanguageStore } from "../../../stores/Language/useLanguageStore";
import useTranslations from "../../../hooks/useTranslations";
import Images from "../../../asset/images";
import { translateProductName } from "../../../utils/translateProductName";

const cx = classNames.bind(styles);

const OrderItem = ({
  order,
  expandedShipping,
  expandedItems,
  toggleShippingInfo,
  toggleOrderItems,
  getStatusColor,
  getStatusText,
  getPaymentStatusText,
  getPaymentMethodText,
}) => {
  const { formatPrice } = useLanguageStore();
  const currentTranslations = useTranslations();

  return (
    <div className={cx("order-item")}>
      <div className={cx("order-header")}>
        <div className={cx("order-info")}>
          <h4>
            {currentTranslations?.Order?.order_number} #{order.order_number}
          </h4>
          <span className={cx("order-date")}>{order.order_date}</span>
        </div>
        <div className={cx("status-group")}>
          <span className={cx("order-status", getStatusColor(order.status))}>
            {getStatusText(order.status)}
          </span>
          <span
            className={cx(
              "payment-status",
              order.payment.status === "paid" ? "success" : "pending"
            )}
          >
            {getPaymentStatusText(order.payment.status)}
          </span>
        </div>
      </div>

      <div className={cx("order-details")}>
        {/* Payment Info */}
        <div className={cx("payment-info")}>
          <div className={cx("payment-method")}>
            <strong>{currentTranslations?.Order?.payment_method}</strong>{" "}
            {getPaymentMethodText(order.payment.method)}
          </div>
          {order.payment.transaction_id && (
            <div className={cx("transaction-info")}>
              <strong>
                {currentTranslations?.PaymentSuccess?.transaction_no}
              </strong>{" "}
              {order.payment.transaction_id}
            </div>
          )}
        </div>

        {/* Shipping Info */}
        <div className={cx("shipping-info")}>
          <div
            className={cx("shipping-header")}
            onClick={() => toggleShippingInfo(order.order_id)}
          >
            <h5>{currentTranslations?.Order?.shipping_info}</h5>
            <ChevronDown
              size={16}
              className={cx("chevron", {
                expanded: expandedShipping[order.order_id],
              })}
            />
          </div>
          {expandedShipping[order.order_id] && (
            <div className={cx("shipping-details")}>
              <p>
                <strong>{currentTranslations?.OrderDetail?.recipient}:</strong>{" "}
                {order.shipping.name}
              </p>
              <p>
                <strong>{currentTranslations?.OrderDetail?.phone}:</strong>{" "}
                {order.shipping.phone}
              </p>
              <p>
                <strong>{currentTranslations?.OrderDetail?.address}:</strong>{" "}
                {order.shipping.address}
                {order.shipping.ward_name && `, ${order.shipping.ward_name}`}
                {order.shipping.district_name &&
                  `, ${order.shipping.district_name}`}
              </p>
              {order.shipping.tracking_code && (
                <p>
                  <strong>
                    {currentTranslations?.OrderDetail?.order_code}:
                  </strong>{" "}
                  {order.shipping.tracking_code}
                </p>
              )}
              {order.shipping.tracking_id && (
                <p>
                  <strong>
                    {currentTranslations?.OrderDetail?.order_tracking}:
                  </strong>{" "}
                  {order.shipping.tracking_id}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Order Items */}
        {order.items && order.items.length > 0 && (
          <div className={cx("order-items")}>
            <div
              className={cx("items-header")}
              onClick={() =>
                order.items.length > 2 && toggleOrderItems(order.order_id)
              }
              style={{ cursor: order.items.length > 2 ? "pointer" : "default" }}
            >
              <h5>
                {currentTranslations?.Order?.products} ({order.items.length}{" "}
                {currentTranslations?.Order?.products})
              </h5>
              {order.items.length > 2 && (
                <ChevronDown
                  size={16}
                  className={cx("chevron", {
                    expanded: expandedItems[order.order_id],
                  })}
                />
              )}
            </div>

            {/* Display items based on expand state */}
            {(expandedItems[order.order_id]
              ? order.items
              : order.items.slice(0, 2)
            ).map((item, index) => (
              <div key={`${item.product_id}-${index}`} className={cx("item")}>
                <div className={cx("item-image")}>


                  <img
                  loading="lazy"
                    src={item.image_url || Images.ErrorImage}

                    alt={item.name}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
                <div className={cx("item-details")}>
                  <span className={cx("item-name")}>{translateProductName(item.name)}</span>
                  <div className={cx("item-meta")}>
                    <span className={cx("item-quantity")}>
                      {currentTranslations?.OrderReview?.product_name}:{" "}
                      {item.quantity}
                    </span>
                    <span className={cx("item-price")}>
                      {formatPrice(item.price)}
                    </span>
                  </div>
                  <div className={cx("item-total")}>
                    <strong>
                      {currentTranslations?.Order?.unit_price}:{" "}
                      {formatPrice(item.total)}
                    </strong>
                  </div>
                </div>
              </div>
            ))}

            {/* Show more button for items > 2 and not expanded */}
            {order.items.length > 2 && !expandedItems[order.order_id] && (
              <div className={cx("show-more-items")}>
                <button
                  className={cx("show-more-btn")}
                  onClick={() => toggleOrderItems(order.order_id)}
                >
                  {currentTranslations?.Order?.view_more_products}{" "}
                  {order.items.length - 2}{" "}
                  {currentTranslations?.Order?.products}
                  <ChevronDown size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Order Summary */}
        <div className={cx("order-summary")}>
          <div className={cx("summary-row")}>
            <span>{currentTranslations?.Order?.subtotal}:</span>
            <span>{formatPrice(order.payment.subtotal)}</span>
          </div>
          {order.payment.shipping_cost > 0 && (
            <div className={cx("summary-row")}>
              <span>{currentTranslations?.Order?.shipping_fee}:</span>
              <span>{formatPrice(order.payment.shipping_cost)}</span>
            </div>
          )}
          <div className={cx("summary-row", "total")}>
            <strong>{currentTranslations?.Order?.total}:</strong>
            <strong>{formatPrice(order.payment.total_amount)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderItem;
